/* Icons.
 *
 * Drawn rather than imported so they can be domain-true and so they inherit
 * stroke weight from one place. Every glyph is 2px minimum at 24px — a
 * hairline icon is the first thing glare eats, same rule as the rules.
 *
 * The set is deliberately narrow. Where an icon would be ambiguous, this app
 * uses a word instead: a label always beats a clever pictogram on a screen
 * being read at arm's length in the sun.
 */

interface Props {
  size?: number;
  className?: string;
}

const box = (size: number) => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
});

/** Field day — beds in a measured section, newest on top. */
export const IconSection = ({ size = 24, className }: Props) => (
  <svg {...box(size)} className={className}>
    <path d="M4 6h13" />
    <path d="M4 12h9" />
    <path d="M4 18h15" />
  </svg>
);

/** Priorities — a ranked stack, longest first. */
export const IconRank = ({ size = 24, className }: Props) => (
  <svg {...box(size)} className={className}>
    <path d="M4 6h16" />
    <path d="M4 12h11" />
    <path d="M4 18h6" />
  </svg>
);

/** Capture. The primary action gets the least clever glyph in the app. */
export const IconPlus = ({ size = 24, className }: Props) => (
  <svg {...box(size)} className={className} strokeWidth={2.75}>
    <path d="M12 5v14M5 12h14" />
  </svg>
);

/** Assigned — a dashed polygon, the same register the map draws assignments in. */
export const IconAssignment = ({ size = 24, className }: Props) => (
  <svg {...box(size)} className={className}>
    <path d="M12 3.5 20 9v7l-8 4.5L4 16V9z" strokeDasharray="3.5 2.5" />
  </svg>
);

/** Map — contours. */
export const IconMap = ({ size = 24, className }: Props) => (
  <svg {...box(size)} className={className}>
    <path d="M2.5 16c3-4 6-4 9 0s6 4 10 0" />
    <path d="M2.5 20c3-4 6-4 9 0" />
    <path d="M6 11c2.5-3.5 5-3.5 7.5 0" />
  </svg>
);

export const IconCamera = ({ size = 24, className }: Props) => (
  <svg {...box(size)} className={className}>
    <path d="M3 8.5A1.5 1.5 0 0 1 4.5 7h2.2l1.2-2h8.2l1.2 2h2.2A1.5 1.5 0 0 1 21 8.5v9A1.5 1.5 0 0 1 19.5 19h-15A1.5 1.5 0 0 1 3 17.5z" />
    <circle cx="12" cy="13" r="3.4" />
  </svg>
);

export const IconMic = ({ size = 24, className }: Props) => (
  <svg {...box(size)} className={className}>
    <rect x="9" y="3" width="6" height="11" rx="3" />
    <path d="M5.5 12a6.5 6.5 0 0 0 13 0M12 18.5V21" />
  </svg>
);

/** Position — the accuracy ring is part of the glyph, because accuracy is
 *  part of the fact. */
export const IconFix = ({ size = 24, className }: Props) => (
  <svg {...box(size)} className={className}>
    <circle cx="12" cy="12" r="8.5" strokeDasharray="2 3" />
    <circle cx="12" cy="12" r="2.5" fill="currentColor" stroke="none" />
  </svg>
);

/** Brunton — the instrument, for the reading-source toggle. */
export const IconCompass = ({ size = 24, className }: Props) => (
  <svg {...box(size)} className={className}>
    <circle cx="12" cy="12" r="9" />
    <path d="m15.2 8.8-1.9 4.5-4.5 1.9 1.9-4.5z" />
  </svg>
);


/** Assignment — a dashed polygon, the register the map draws intent in. */
export const IconPolygon = ({ size = 24, className }: Props) => (
  <svg {...box(size)} className={className}>
    <path d="M12 3.5 20 9v7l-8 4.5L4 16V9z" strokeDasharray="3.5 2.5" />
  </svg>
);

/** Rock observation — bedded ground. */
export const IconStrata = ({ size = 24, className }: Props) => (
  <svg {...box(size)} className={className}>
    <path d="M4 7h16M4 12h16M4 17h16" strokeDasharray="2.5 2" />
  </svg>
);

/** Layers, for the map. */
export const IconLayers = ({ size = 24, className }: Props) => (
  <svg {...box(size)} className={className}>
    <path d="M3 7.5 12 3l9 4.5-9 4.5z" />
    <path d="m3 12 9 4.5L21 12M3 16.5 12 21l9-4.5" />
  </svg>
);

export const IconChevron = ({ size = 24, className }: Props) => (
  <svg {...box(size)} className={className}>
    <path d="m9 5 7 7-7 7" />
  </svg>
);

export const IconCheck = ({ size = 24, className }: Props) => (
  <svg {...box(size)} className={className} strokeWidth={2.75}>
    <path d="m4.5 12.5 5 5 10-11" />
  </svg>
);

export const IconArrowUp = ({ size = 24, className }: Props) => (
  <svg {...box(size)} className={className}>
    <path d="M12 19V5M6 11l6-6 6 6" />
  </svg>
);

export const IconClose = ({ size = 24, className }: Props) => (
  <svg {...box(size)} className={className}>
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

export const IconGrip = ({ size = 24, className }: Props) => (
  <svg {...box(size)} className={className}>
    <path d="M8 8h.01M8 12h.01M8 16h.01M16 8h.01M16 12h.01M16 16h.01" strokeWidth={2.75} />
  </svg>
);
